import { useCallback, useEffect, useRef, useState } from "react";

import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import {
  Alert,
  Button,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";
import { useNavigate } from "react-router-dom";

import { useAuthContext } from "hooks";

import {
  BLOCKED_REASON_MESSAGES,
  CORRECTABLE_OUTCOME_MESSAGES,
  DEFAULT_BLOCKED_MESSAGE,
  DEFAULT_CORRECTABLE_MESSAGE,
  EXISTING_QUOTE_MESSAGES,
  LINK_REJECTED_HTTP_STATUS,
  LINK_REJECTED_MESSAGE,
  PREVIEW_UNAVAILABLE_MESSAGE,
  previewFailureCanBeRetried,
  RECOVERABLE_OUTCOME_MESSAGES,
  SUBMIT_QUOTE_INTENT_QUERY_PARAM_KEY,
  SUBMIT_QUOTE_NO_CAPACITY_INTENT,
} from "./constants";
import { ShipmentDetailCard } from "./ShipmentDetailCard";
import { ExistingQuoteStateValue } from "./types";
import { UnauthenticatedMultiShipmentSubmitQuoteFormContainer } from "./UnauthenticatedMultiShipmentSubmitQuoteFormContainer";
import { UnauthenticatedNoCapacityButtonContainer } from "./UnauthenticatedNoCapacityButtonContainer";
import { UnauthenticatedSubmitQuoteFormContainer } from "./UnauthenticatedSubmitQuoteFormContainer";
import {
  ClientAppEvent,
  COMMAND_LINK_TOKEN_QUERY_PARAM_KEY,
  CommandLinkName,
  CommandLinkUtmCampaign,
  HANDOFF_SCREEN_UTM_CAMPAIGNS,
  HandoffScreen,
} from "../../../../constants";
import { ErrorTypography } from "../../../ErrorTypography/ErrorTypography";
import { LoadingWrappedNode } from "../../../LoadingWrappedNode";
import { useCommandLinkPreview } from "../../hooks/useCommandLinkPreview";
import { buildShipmentDetailUrlWithCommandLinkUtm } from "../../utils";

import type { SubmitQuotePreviewData } from "./types";

interface SubmitQuoteLandingProps {
  token: string;
  // Set by the email's "No Truck Available" button; relabels and scrolls to that control.
  intent: string | null;
}

interface AppHandoffProps {
  message: string;
  label: string;
  handoffScreen: HandoffScreen;
}

/**
 * Landing for the emailed submit-quote command link. Renders the shipment the
 * carrier was matched to and, when they are eligible and hold no live quote, the
 * same quote form the app uses
 * (docs/engineering/design/carrier_submit_quote_command_link_design.md D2, D4, D6).
 */
export function SubmitQuoteLanding(props: SubmitQuoteLandingProps) {
  const { token, intent } = props;
  const posthog = usePostHog();
  const navigate = useNavigate();
  /**
   * A logged-in visitor belongs in the app, not on this logged-out page. They are
   * sent to the shipment once the preview names it — the id lives only inside the
   * signed token — whoever they are logged in as: the in-app page applies its own
   * access rules (docs/engineering/design/carrier_submit_quote_command_link_no_capacity_design.md D5).
   */
  const { isAuthenticated } = useAuthContext();
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [recoverableOutcomeCode, setRecoverableOutcomeCode] = useState<
    string | null
  >(null);
  /**
   * A stop reported by the submission rather than by the preview — the shipment
   * was cancelled, booked or expired between opening this page and pressing
   * Submit. Held separately from the preview's own reason because the preview
   * that is already on screen said the carrier could quote.
   */
  const [submittedBlockedReasonCode, setSubmittedBlockedReasonCode] = useState<
    string | null
  >(null);
  const visitLoggedRef = useRef(false);
  const previewLoggedRef = useRef(false);

  const { data, loading, error, failureStatus, failureCode } =
    useCommandLinkPreview<SubmitQuotePreviewData>({
      commandName: CommandLinkName.SUBMIT_QUOTE,
      token,
    });
  const linkWasRejected = failureStatus === LINK_REJECTED_HTTP_STATUS;
  // Which copy the carrier gets — a separate question from `linkWasRejected`, which
  // labels the funnel: 400 and 404 are refusals the funnel does not call "invalid",
  // but they are just as permanent as a 403.
  const previewCanBeRetried = previewFailureCanBeRetried(failureStatus);

  /**
   * While the token is still in the URL, no outbound request may carry it in a
   * Referer header. Scoped to this page rather than set app-wide so the rest of
   * the app keeps its referrer behaviour
   * (docs/engineering/design/carrier_submit_quote_command_link_design.md §10).
   */
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  /**
   * The token is a bearer credential with quote-write authority. Drop it from the
   * address bar and from forward history as soon as this page mounts — it is
   * already held as a prop, so nothing downstream reads it from the URL. This runs
   * before the analytics effects below because posthog-js stamps `$current_url`
   * on every event it sends
   * (docs/engineering/design/carrier_submit_quote_command_link_design.md §10).
   */
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(COMMAND_LINK_TOKEN_QUERY_PARAM_KEY)) {
      return;
    }
    url.searchParams.delete(COMMAND_LINK_TOKEN_QUERY_PARAM_KEY);
    url.searchParams.delete(SUBMIT_QUOTE_INTENT_QUERY_PARAM_KEY);
    window.history.replaceState(null, "", `${url.pathname}${url.search}`);
  }, []);

  // The landing funnel counts logged-out visits only: a logged-in visitor is
  // redirected, and attributed by the redirect's UTM campaign instead.
  useEffect(() => {
    if (visitLoggedRef.current || isAuthenticated) {
      return;
    }
    visitLoggedRef.current = true;
    posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_VISITED, {
      has_token: true,
    });
  }, [posthog, isAuthenticated]);

  useEffect(() => {
    if (previewLoggedRef.current || loading || isAuthenticated) {
      return;
    }
    previewLoggedRef.current = true;
    if (data == null) {
      posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_PREVIEW_FAILED, {
        // A 403 is the backend refusing the link — an expired or tampered token, or
        // a carrier who lost read access to the shipment; anything else is a
        // transport or server problem. Separating them is what makes the TTL
        // measurable (docs/engineering/design/carrier_submit_quote_command_link_design.md §8).
        // Read from the status rather than the body: a 403 carries no message.
        failure_reason: linkWasRejected ? "invalid" : "error",
        // `failure_reason` buckets a rate limit, a 404 and an abandoned page
        // together as "error", which left this page's failure rate unexplainable
        // (issue #3189 F1). Read the two together: a status means the backend
        // answered and the number says how; no status with `ECONNABORTED` is a
        // page the visitor left mid-request; no status and no code is an API
        // that could not be reached. `failure_status` is left off entirely
        // rather than sent as a string, so it stays numeric and a filter like
        // `>= 500` works.
        ...(failureStatus != null ? { failure_status: failureStatus } : {}),
        failure_code: failureCode ?? "none",
      });
      return;
    }
    posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_PREVIEWED, {
      shipment_id: data.shipment.info.id,
      shipment_type: data.shipment.info.type,
      can_quote: data.eligibility.can_quote,
      existing_quote_state: data.existing_quote.state ?? "none",
      answered_question_count: data.answered_question_count,
      intent: intent ?? "none",
      has_marked_no_capacity: data.has_marked_no_capacity,
    });
    if (!data.eligibility.can_quote) {
      posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_BLOCKED_VIEW, {
        shipment_id: data.shipment.info.id,
        reason_code: data.eligibility.reason_code,
      });
    }
  }, [
    data,
    loading,
    linkWasRejected,
    failureStatus,
    failureCode,
    posthog,
    isAuthenticated,
    intent,
  ]);

  useEffect(() => {
    if (!isAuthenticated || data == null) {
      return;
    }
    // `replace`: the token is already stripped from this URL, so going Back to
    // it would only show an invalid link.
    navigate(
      buildShipmentDetailUrlWithCommandLinkUtm(
        data.shipment.info.id,
        CommandLinkUtmCampaign.SUBMIT_QUOTE_LOGGED_IN_REDIRECT,
      ),
      { replace: true },
    );
  }, [isAuthenticated, data, navigate]);

  const onQuoteSubmitted = useCallback(() => {
    setQuoteSubmitted(true);
  }, []);

  const onBlockedFailure = useCallback((blockedReasonCode: string) => {
    setSubmittedBlockedReasonCode(blockedReasonCode);
  }, []);

  // Nothing is passed underneath the shared loading state: every element on this
  // page comes from the preview, so there is no layout to skeleton until it lands.
  if (loading || (isAuthenticated && data != null)) {
    return <LoadingWrappedNode loading>{null}</LoadingWrappedNode>;
  }

  if (error != null || data == null) {
    return (
      <ErrorTypography
        error={
          previewCanBeRetried
            ? PREVIEW_UNAVAILABLE_MESSAGE
            : LINK_REJECTED_MESSAGE
        }
      />
    );
  }

  const { shipment, existing_quote: existingQuote, eligibility } = data;
  const shipmentId = shipment.info.id;
  const onQuestionsClicked = () => {
    posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_QA_OPENED, {
      shipment_id: shipmentId,
      answered_question_count: data.answered_question_count,
    });
  };

  const onTeammateQuotesClicked = () => {
    posthog?.capture(
      ClientAppEvent.QUOTE_COMMAND_LINK_TEAMMATE_QUOTES_CLICKED,
      {
        shipment_id: shipmentId,
        teammate_quote_count: shipment.teammate_quote_count,
      },
    );
  };

  const onBookNowClicked = () => {
    posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_BOOK_NOW_CLICKED, {
      shipment_id: shipmentId,
    });
  };

  // The capture and the UTM campaign both follow from `handoffScreen`, so a caller
  // cannot ship a handoff untagged, or tag the event and the link differently.
  const renderAppHandoff = (handoffProps: AppHandoffProps) => {
    const { message, label, handoffScreen } = handoffProps;
    const onClick = () => {
      posthog?.capture(ClientAppEvent.QUOTE_COMMAND_LINK_MANAGE_QUOTE_CLICKED, {
        shipment_id: shipmentId,
        existing_quote_state: existingQuote.state ?? "none",
        handoff_screen: handoffScreen,
      });
    };
    return (
      <Stack spacing={1.5} alignItems="center">
        <Typography variant="body1" textAlign="center">
          {message}
        </Typography>
        <Tooltip title="Opens your Evotrux account, where you can review and update this quote">
          <Button
            variant="contained"
            href={buildShipmentDetailUrlWithCommandLinkUtm(
              shipmentId,
              HANDOFF_SCREEN_UTM_CAMPAIGNS[handoffScreen],
            )}
            onClick={onClick}
          >
            {label}
          </Button>
        </Tooltip>
      </Stack>
    );
  };

  /**
   * One renderer for both sources of a hard stop — the preview's eligibility and
   * a submission that came back blocked — so the carrier reads the same sentence
   * either way. Before this, a shipment cancelled mid-flow fell through to the
   * generic transport-error copy while reloading the same link said the shipper
   * had cancelled it (#2975 §2).
   */
  const renderBlockedReason = (reasonCode: string | null) => (
    <Typography variant="body1" textAlign="center">
      {(reasonCode != null ? BLOCKED_REASON_MESSAGES[reasonCode] : null) ??
        DEFAULT_BLOCKED_MESSAGE}
    </Typography>
  );

  const noCapacityAction = (
    <UnauthenticatedNoCapacityButtonContainer
      token={token}
      shipmentId={shipmentId}
      shipperCompanyId={shipment.shipment_company.id}
      initialHasMarkedNoCapacity={data.has_marked_no_capacity}
      arrivedFromNoCapacityEmail={intent === SUBMIT_QUOTE_NO_CAPACITY_INTENT}
      onRecoverableFailure={setRecoverableOutcomeCode}
      onBlockedFailure={onBlockedFailure}
    />
  );

  const renderQuotingArea = () => {
    if (quoteSubmitted) {
      return renderAppHandoff({
        message:
          "Your quote is in. The shipper has been notified and will respond through Evotrux.",
        label: "Log in to view your quote",
        handoffScreen: HandoffScreen.QUOTE_SUBMITTED,
      });
    }

    /**
     * A stop the submission discovered. Checked before the preview's own
     * eligibility below, because this one is newer: the preview on screen said
     * the carrier could quote, and the shipment changed underneath them.
     */
    if (submittedBlockedReasonCode != null) {
      return renderBlockedReason(submittedBlockedReasonCode);
    }

    if (recoverableOutcomeCode != null) {
      const handoffMessage =
        RECOVERABLE_OUTCOME_MESSAGES[recoverableOutcomeCode];
      if (handoffMessage != null) {
        return renderAppHandoff({
          message: handoffMessage,
          label: "Log in to view your quote",
          handoffScreen: HandoffScreen.RECOVERABLE_OUTCOME,
        });
      }
    }

    /**
     * Anything else recoverable is something the carrier can correct here, so the
     * form stays mounted with the reason on it rather than swallowing the
     * rejection (execute reports these as HTTP 200).
     */
    const submissionError =
      recoverableOutcomeCode != null
        ? (CORRECTABLE_OUTCOME_MESSAGES[recoverableOutcomeCode] ??
          DEFAULT_CORRECTABLE_MESSAGE)
        : null;

    if (existingQuote.state != null) {
      return renderAppHandoff({
        message: EXISTING_QUOTE_MESSAGES[existingQuote.state],
        label:
          existingQuote.state === ExistingQuoteStateValue.COUNTERED
            ? "Log in to respond"
            : "Log in to view or update your quote",
        handoffScreen: HandoffScreen.EXISTING_QUOTE,
      });
    }

    if (!eligibility.can_quote) {
      return renderBlockedReason(eligibility.reason_code);
    }

    if (shipment.info.type === "MULTI_SHIPMENT_PROJECT") {
      return (
        <UnauthenticatedMultiShipmentSubmitQuoteFormContainer
          shipment={shipment}
          token={token}
          onQuoteSubmitted={onQuoteSubmitted}
          onRecoverableFailure={setRecoverableOutcomeCode}
          onBlockedFailure={onBlockedFailure}
          submissionError={submissionError}
          secondaryAction={noCapacityAction}
        />
      );
    }

    return (
      <UnauthenticatedSubmitQuoteFormContainer
        shipment={shipment}
        token={token}
        onQuoteSubmitted={onQuoteSubmitted}
        onRecoverableFailure={setRecoverableOutcomeCode}
        onBlockedFailure={onBlockedFailure}
        submissionError={submissionError}
        secondaryAction={noCapacityAction}
      />
    );
  };

  // The quoting area leads the page, above the shipment card, so its buttons are in
  // view on arrival rather than below the whole card.
  return (
    <Stack spacing={2}>
      {shipment.is_book_now && (
        <Alert
          severity="info"
          icon={<BoltOutlinedIcon fontSize="small" />}
          action={
            <Tooltip title="Opens this shipment in your Evotrux account, where you can take the shipper's Book Now price">
              <Button
                size="small"
                href={buildShipmentDetailUrlWithCommandLinkUtm(
                  shipmentId,
                  CommandLinkUtmCampaign.SUBMIT_QUOTE_BOOK_NOW,
                )}
                onClick={onBookNowClicked}
              >
                Log in to secure it
              </Button>
            </Tooltip>
          }
        >
          {/*
            The price itself stays behind login: this page is authorized by a
            forwardable token, and the notice is actionable without it.
          */}
          This load is Book Now — it can be booked outright instead of quoted.
        </Alert>
      )}
      {shipment.teammate_quote_count > 0 && (
        <Alert
          severity="info"
          icon={<GroupsOutlinedIcon fontSize="small" />}
          action={
            <Tooltip title="Opens this shipment in your Evotrux account, where your colleagues' quotes are shown">
              <Button
                size="small"
                href={buildShipmentDetailUrlWithCommandLinkUtm(
                  shipmentId,
                  CommandLinkUtmCampaign.SUBMIT_QUOTE_TEAMMATES,
                )}
                onClick={onTeammateQuotesClicked}
              >
                Log in to view
              </Button>
            </Tooltip>
          }
        >
          {/*
            A count, not names: the preview deliberately withholds who quoted,
            because this page is reachable by anyone the email was forwarded to.
          */}
          {shipment.teammate_quote_count === 1
            ? "1 quote has already been placed by someone from your company."
            : `${shipment.teammate_quote_count} quotes have already been placed by people from your company.`}
        </Alert>
      )}
      {data.answered_question_count > 0 && (
        <Tooltip title="Opens your Evotrux account, where the shipper's answers are shown on the shipment">
          <Button
            startIcon={<ForumOutlinedIcon />}
            variant="outlined"
            href={buildShipmentDetailUrlWithCommandLinkUtm(
              shipmentId,
              CommandLinkUtmCampaign.SUBMIT_QUOTE_QA,
            )}
            onClick={onQuestionsClicked}
          >
            {data.answered_question_count === 1
              ? "1 question answered — log in to view"
              : `${data.answered_question_count} questions answered — log in to view`}
          </Button>
        </Tooltip>
      )}
      {renderQuotingArea()}
      <Divider />
      <ShipmentDetailCard shipment={shipment} />
    </Stack>
  );
}
